// FirmwareIdentifierModal — "what is this build?"
//
// Drop a firmware file (or type a git hash) and it:
//   1. looks for a published build (by sha256 of the file, then by the git hash
//      extracted from it / typed) — match → offer to open its page;
//   2. if there's no match but a file was dropped, extracts and shows whatever it
//      can (git hash, version, branch, architecture, sha256) so an unpublished /
//      mystery binary can still be identified.
//
// All extraction is client-side (mirrors the upload modal) — no upload happens.

import React, { useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';
import { Panel } from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import DropZone from '../components/ui/DropZone';
import { Field as TextField } from '../components/ui/Field';
import { Link } from 'react-router-dom';
import { useToast } from '../components/Toast/ToastContext';
import { useFirmwareApi, shortSha, firmwareShortHash, formatBytes } from './firmwareApi';
import { clientSha256, autoDetectMeta, type DetectedMeta } from './firmwareParse';
import { firmwarePath } from './types';
import type { ArchPreset, Firmware } from './types';

interface Props {
  archPresets: ArchPreset[];
  onClose: () => void;
}

const MAX_BYTES = 50 * 1024 * 1024;

type Result =
  | { kind: 'file'; fileName: string; size: number; sha: string; meta: DetectedMeta; match: Firmware | null }
  | { kind: 'hash'; query: string; match: Firmware | null };

const Field: React.FC<{ label: string; value?: string | null; mono?: boolean }> = ({ label, value, mono = true }) => (
  <div className="grid grid-cols-[6.5rem_1fr] gap-3 py-1.5 border-b border-gray-800/60 text-sm">
    <span className="text-on-surface-variant">{label}</span>
    <span className={`text-on-surface break-all min-w-0 ${mono ? 'font-mono' : ''}`}>{value || <span className="text-on-surface-disabled">—</span>}</span>
  </div>
);

export default function FirmwareIdentifierModal({ archPresets, onClose }: Props) {
  const toast = useToast();
  const fw = useFirmwareApi();
  const [busy, setBusy] = useState(false);
  const [hashInput, setHashInput] = useState('');
  const [result, setResult] = useState<Result | null>(null);
  const reqId = useRef(0); // ignore stale results if the user drops a second file mid-analysis

  // Resolve a published build by any of the candidate hashes (first hit wins).
  const findPublished = async (hashes: string[]): Promise<Firmware | null> => {
    for (const h of hashes.filter((x) => x && x.length >= 7)) {
      try { return await fw.resolveByHash(h); } catch { /* 404 — try the next */ }
    }
    return null;
  };

  const identifyFile = async (file: File | null) => {
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error('File exceeds the 50 MB limit'); return; }
    const id = ++reqId.current;
    setBusy(true);
    setResult(null);
    try {
      const [sha, meta] = await Promise.all([clientSha256(file), autoDetectMeta(file, archPresets)]);
      // by exact file (sha256) first, then by the commit it was built from (git hash)
      const match = await findPublished([sha.slice(0, 16), meta.gitHash || '']);
      if (id === reqId.current) setResult({ kind: 'file', fileName: file.name, size: file.size, sha, meta, match });
    } catch {
      if (id === reqId.current) toast.error('Could not read that file');
    } finally {
      if (id === reqId.current) setBusy(false);
    }
  };

  const identifyHash = async () => {
    const q = hashInput.trim().toLowerCase();
    if (q.length < 7) { toast.error('Enter at least 7 hash characters'); return; }
    const id = ++reqId.current; // shares the counter with identifyFile so they cross-invalidate
    setBusy(true);
    setResult(null);
    try {
      const match = await findPublished([q]);
      if (id === reqId.current) setResult({ kind: 'hash', query: q, match });
    } finally {
      if (id === reqId.current) setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} size="lg" title="Firmware Identifier">
      <div className="space-y-4">
          {/* Dropzone */}
          <DropZone
            what="a firmware file"
            hint="Drop a .px4, .bin or .elf file"
            busy={busy ? 'Analyzing…' : undefined}
            onFiles={(files) => identifyFile(files[0] || null)}
            inputProps={{ 'aria-label': 'Drop or choose a firmware file to identify' }}
          />

          {/* Hash entry */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-on-surface-variant flex-none">or hash</span>
            <TextField
              className="flex-1 font-mono"
              value={hashInput}
              onChange={(e) => setHashInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') identifyHash(); }}
              placeholder="short or full git hash / SHA-256"
              aria-label="Git hash or SHA-256"
              disabled={busy}
            />
            <button
              onClick={identifyHash}
              disabled={busy || hashInput.trim().length < 7}
              className={`${buttonClass('filled', 'md')} flex-none`}
            >
              Identify
            </button>
          </div>

          {/* Result */}
          <Reveal show={!!result}>
            {result && (
            <Panel label="Result" on="panel" padding="sm">
              {result.match ? (
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <p className="text-sm text-green-300 font-semibold">On the hub</p>
                    <p className="text-sm text-white truncate">{result.match.label}</p>
                  </div>
                  <Link
                    to={firmwarePath(result.match.category, firmwareShortHash(result.match))}
                    onClick={onClose}
                    className={`${buttonClass('filled', 'md')} flex-none`}
                  >
                    Open build →
                  </Link>
                </div>
              ) : (
                <p className="text-sm text-on-surface-variant mb-2">
                  {result.kind === 'file' ? 'Not on the hub — identification only:' : 'No build on the hub matches that hash.'}
                </p>
              )}

              {result.kind === 'file' && (
                <div className="mt-1">
                  <Field label="File" value={`${result.fileName}  ·  ${formatBytes(result.size)}`} />
                  <Field label="Git hash" value={result.meta.gitHash} />
                  <Field label="Version" value={result.meta.version} />
                  <Field label="Branch" value={result.meta.branch} />
                  <Field label="Architecture" value={result.meta.architecture} />
                  <Field label="SHA-256" value={result.sha} />
                  {!result.meta.gitHash && !result.meta.version && !result.meta.branch && (
                    <p className="text-xs text-on-surface-variant mt-2 italic">No PX4 version info found in this file (only its SHA-256).</p>
                  )}
                </div>
              )}

              {result.kind === 'hash' && result.match && (
                <Field label="Git hash" value={result.match.git_hash} />
              )}
              {result.kind === 'hash' && !result.match && (
                <p className="text-xs text-on-surface-variant italic">Drop the firmware file instead to extract its branch and version.</p>
              )}
            </Panel>
            )}
          </Reveal>
      </div>
    </Dialog>
  );
}
